import React, { useState } from 'react';
import {
  ClipboardIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const STATUS_STYLES = {
  verified: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  not_started: 'bg-slate-100 text-slate-600',
  failure: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
  verified: 'Verificado',
  pending: 'Propagando',
  not_started: 'Sin empezar',
  failure: 'Fallido',
};

/**
 * Un campo con su botón de copiar.
 *
 * El valor va en **una sola línea truncada**, no entero. Un DKIM son ~400
 * caracteres sin espacios: enseñarlo completo en una columna estrecha lo parte
 * carácter a carácter y convierte la fila en una cinta vertical ilegible. Y no
 * hace falta — nadie lo lee, se copia.
 */
const Field = ({ label, value, context, mono = true }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Sin portapapeles (navegador viejo, o http sin permisos): el valor sigue
      // seleccionable a mano. No se molesta al usuario con un error por esto.
    }
  };

  return (
    <div>
      <p className='text-xs text-slate-500 mb-0.5'>{label}</p>
      <div className='flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1.5'>
        <span
          title={value}
          className={`flex-1 min-w-0 truncate text-slate-800 ${mono ? 'font-mono text-xs' : 'text-sm'}`}
        >
          {value}
        </span>
        <button
          type='button'
          onClick={copy}
          title='Copiar'
          /* Con cuatro registros, un «Copiar Nombre» repetido cuatro veces es
             inútil para un lector de pantalla. Se identifica por el nombre del
             registro, que es corto — y NO por el valor, que en un DKIM son ~400
             caracteres. */
          aria-label={context ? `Copiar ${label.toLowerCase()} de ${context}` : `Copiar ${label}`}
          className='shrink-0 p-1 text-slate-400 hover:text-blue-600 rounded-lg'
        >
          {copied ? (
            <CheckIcon className='w-4 h-4 text-green-600' />
          ) : (
            <ClipboardIcon className='w-4 h-4' />
          )}
        </button>
      </div>
    </div>
  );
};

/**
 * Los registros DNS a publicar, **uno por tarjeta** y con su estado propio.
 *
 * Tarjetas y no tabla: esto vive en la columna estrecha del perfil, y una tabla
 * de cuatro columnas ahí se sale y parte los valores. Es además el patrón que
 * usan Resend, Vercel o Cloudflare para lo mismo.
 *
 * El estado va **registro a registro**: un genérico «no verificado» no le dice
 * al informático del cliente cuál de los cuatro le falta, que es lo único que
 * necesita saber.
 */
const DnsRecordsList = ({ records = [] }) => {
  if (!records.length) return null;

  return (
    <div className='space-y-3'>
      {/* Este aviso vale más que el resto de la pantalla: casi todos los paneles
          DNS quieren el nombre RELATIVO, y pegar el dominio completo es el error
          que va a generar la mayoría de los tickets. */}
      <div className='flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2'>
        <ExclamationTriangleIcon className='w-4 h-4 text-amber-600 shrink-0 mt-0.5' />
        <p className='text-xs text-amber-800'>
          <span className='font-medium'>Pega el nombre tal cual aparece aquí.</span> Casi
          todos los paneles de DNS esperan el nombre{' '}
          <span className='font-medium'>relativo</span> y añaden el dominio ellos solos. Si
          escribes el dominio completo, la verificación no pasará.
        </p>
      </div>

      {records.map((r, i) => (
        <div
          key={`${r.type}-${r.name}-${i}`}
          className='border border-slate-200 rounded-lg p-3 space-y-2 bg-white'
        >
          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2 min-w-0'>
              <span className='text-xs font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded-lg'>
                {r.type}
              </span>
              {r.record && <span className='text-xs text-slate-500 truncate'>{r.record}</span>}
              {r.priority != null && (
                <span className='text-xs text-slate-400 whitespace-nowrap'>
                  prioridad {r.priority}
                </span>
              )}
            </div>
            <span
              className={`text-xs px-2 py-1 rounded-lg whitespace-nowrap ${
                STATUS_STYLES[r.status] || STATUS_STYLES.not_started
              }`}
            >
              {STATUS_LABELS[r.status] || r.status}
            </span>
          </div>

          <Field label='Nombre' value={r.name} context={r.name} />
          <Field label='Valor' value={r.value} context={r.name} />
        </div>
      ))}
    </div>
  );
};

export default DnsRecordsList;
